import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from './utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white shadow hover:bg-primary/90',
        destructive: 'bg-danger text-white shadow-sm hover:bg-danger/90',
        outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        outlineDanger: 'border border-danger text-danger shadow-sm hover:bg-danger/10',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        linkOutline: 'text-primary underline-offset-4 hover:underline border border-primary',
        disabled: 'opacity-50 pointer-events-none',
      },
      size: {
        default: 'h-12 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 text-md',
        lg: 'h-12 px-4',
        icon: 'h-9 w-9',
        circle: 'h-9 w-9 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    icon?: React.ComponentProps<typeof FontAwesomeIcon>['icon']
    iconPosition?: 'left' | 'right'
    iconClassName?: string
  }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, icon, iconPosition = 'left', iconClassName, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    const renderContent = () => {
      if (!icon) return children

      const iconElement = <FontAwesomeIcon icon={icon} className={iconClassName} size={'lg'} />

      return iconPosition === 'left' ? (
        <>
          {iconElement}
          {children}
        </>
      ) : (
        <>
          {children}
          {iconElement}
        </>
      )
    }

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {renderContent()}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
